import type {
  ListColumnOptions,
  ListContextValue,
  ListData,
  ListItem,
  ListLazyLoadingInfos,
  ListProviderProps,
} from "./list.type";
import type {
  TableViewFilterKey,
  TableViewFilterValue,
} from "@/entities/table-view";

import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { useState, useMemo } from "react";

import { TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { useOrganizationContextListFilter } from "@/modules/organization/organization-context-list-filter";

import { useListParamsHandler } from "./list-params";
import { useListContext } from "./list.context";
import {
  convertFiltersToFetchFilters,
  getListCleanFetchParams,
  getListVisibleSorts,
} from "./list.helper";

export const useListFilterValues = (filterKey: TableViewFilterKey) => {
  const { filters } = useListContext();

  return useMemo(
    () => get(filters, filterKey, []) as TableViewFilterValue[],
    [filters, filterKey]
  );
};

export const useListMetadata = <Metadata = unknown>(
  path: string,
  defaultValue: Metadata
) => {
  const { metadata } = useListContext();

  return useMemo(
    () => get(metadata, path, defaultValue) as Metadata,
    [metadata, path, defaultValue]
  );
};

export const useListContextValues = <
  DataItem extends ListItem = ListItem,
  Metadata = unknown,
>({
  endpoint = "",
  storageKey,
  contextTabValue,
  contextTabKey = TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
  includes = [],
  withMetadata = [],
  defaultSort = ["-updated_at"],
  multipleSort = true,
  hiddenFilters = {},
  withContext = false,
  loading = false,
  enabled = true,
  neverStale = false,
  parentId = null,
  lazy = false,
  pageSize = 10,
}: ListProviderProps): ListContextValue<DataItem, Metadata> => {
  const { contextFilter } = useOrganizationContextListFilter({ withContext });

  const [columnsOptions, _setColumnsOptions] = useState<ListColumnOptions[]>(
    []
  );
  const columnsCount = columnsOptions?.length ?? 0;

  const [listData, _setListData] = useState<ListData<DataItem, Metadata>>({
    loading: false,
    fetching: false,
    data: [],
    total: 0,
    metadata: {},
  });

  const [listLazyInfos, setListLazyInfos] = useState<ListLazyLoadingInfos>({
    hasNextPage: false,
    fetchNextPage: undefined,
  });

  const {
    sort,
    filters,
    pagination,
    clearFilter,
    clearFilters,
    clearSort,
    clearFiltersAndSorts,
    resetFiltersAndSorts,
    updateFilterValues,
    updateSearchParams,
  } = useListParamsHandler({
    storageKey,
    contextTabKey,
    contextTabValue,
    defaultSort,
    defaultPageSize: pageSize,
  });

  const visibleSort = getListVisibleSorts({ sort, columns: columnsOptions });

  const setListData = (data: ListData<DataItem, Metadata>) => {
    if (!isEqual(data, listData)) _setListData(data);
  };

  const setColumnsOptions = (columns: ListColumnOptions[]) => {
    if (!isEqual(columnsOptions, columns)) _setColumnsOptions(columns);
  };

  const fetchParams = useMemo(() => {
    const { current: pageNumber, pageSize } = pagination;
    return getListCleanFetchParams({
      include: includes.join(","),
      with: withMetadata.join(","),
      sort: sort.length > 0 ? sort.join(",") : defaultSort, // always fallback to `defaultSort` to avoid api random sorting
      ...hiddenFilters,
      ...contextFilter,
      ...convertFiltersToFetchFilters(filters),
      "page[number]": pageNumber,
      "page[size]": pageSize,
    });
  }, [
    includes,
    withMetadata,
    hiddenFilters,
    contextFilter,
    defaultSort,
    sort,
    filters,
    pagination,
  ]);

  const onFetchNextPage = () => {
    const { hasNextPage, fetchNextPage } = listLazyInfos;
    const { loading } = listData;
    if (hasNextPage && !loading) fetchNextPage?.();
  };

  return {
    // external params
    endpoint,
    includes,
    defaultPageSize: pageSize,
    withMetadata,
    defaultSort,
    multipleSort,
    enabled,
    neverStale,
    parentId,
    lazy,
    // internal params
    filters,
    sort,
    pagination,
    visibleSort,
    fetchParams,
    ...listData,
    loading: loading || listData.loading,
    setListData,
    setListLazyInfos,
    columnsCount,
    columnsOptions,
    setColumnsOptions,
    // external fn & callbacks
    clearFilter,
    clearFilters,
    clearSort,
    clearFiltersAndSorts,
    resetFiltersAndSorts,
    onFilterChange: updateFilterValues,
    onParamsChange: updateSearchParams,
    onFetchNextPage,
  };
};
